import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import SectionCard from "@/components/customers/SectionCard";
import { useFreelancer } from "@/hooks/use-freelancers";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useFreelancerProjectHistory, useFreelancerProjectsEnabled } from "@/hooks/use-freelancer-projects";
import { useFreelancerContacts } from "@/hooks/use-freelancer-contacts";
import { FreelancerHero, FreelancerQuickActions } from "@/components/freelancers/FreelancerHero";
import { FreelancerActionsMenu } from "@/components/freelancers/FreelancerActionsMenu";
import { FreelancerFormDialog } from "@/components/freelancers/FreelancerFormDialog";
import { FreelancerReviewsSection } from "@/components/freelancers/FreelancerReviewsSection";
import { FreelancerCommentsSection } from "@/components/freelancers/FreelancerCommentsSection";
import { FreelancerProjectsCard, FreelancerProjectsEmpty } from "@/components/freelancers/FreelancerProjectsCard";
import { FreelancerContactsSection } from "@/components/freelancers/FreelancerContactsSection";
import { FreelancerLinksSection } from "@/components/freelancers/FreelancerLinksSection";
import { FreelancerSupplierCard } from "@/components/freelancers/FreelancerSupplierCard";

/** /frilansere/:id (VIB-2925, layout VIB-2934). Orkestrerer profilens kort; logikken bor i komponentene og hookene. */
export default function FreelancerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: freelancer, isLoading, error } = useFreelancer(id);
  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState(0);
  const projectsOn = useFreelancerProjectsEnabled();
  const enabled = useFeatureFlag("feature_freelancer_bank");
  const { data: contacts = [] } = useFreelancerContacts(id);
  const { data: historyData } = useFreelancerProjectHistory(id, projectsOn);
  const history = projectsOn ? historyData ?? [] : null;

  if (!enabled) return <div className="page-container"><p className="text-muted-foreground">Ikke tilgjengelig ennå.</p></div>;

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SrIcon name="loader2" className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !freelancer) {
    return (
      <div className="page-container space-y-3">
        <p className="text-destructive">{error ? `Feil: ${(error as Error).message}` : "Fant ikke frilanseren. Den kan være slettet."}</p>
        <Link to="/frilansere" className="text-sm text-primary hover:underline">Til frilanserlisten</Link>
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in space-y-6">
      <Link to="/frilansere" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
        <SrIcon name="arrow-left" className="h-4 w-4" />
        Frilansere
      </Link>

      <FreelancerHero
        freelancer={freelancer}
        contacts={contacts}
        history={history}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => { setEditKey((k) => k + 1); setEditOpen(true); }}>
              <SrIcon name="edit" className="h-4 w-4" />
              Rediger
            </Button>
            <FreelancerActionsMenu freelancer={freelancer} />
          </>
        }
      />

      {history && history.length > 0 && <FreelancerProjectsCard rows={history} />}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,26rem)]">
        <FreelancerCommentsSection freelancerId={freelancer.id} />
        <div className="flex flex-col gap-5">
          <FreelancerContactsSection freelancer={freelancer} contacts={contacts} />
          {projectsOn && <FreelancerSupplierCard freelancerId={freelancer.id} />}
          <SectionCard title="Internt notat" compact empty={freelancer.notes ? undefined : "Ingen notat"}>
            {freelancer.notes && <p className="whitespace-pre-line px-4 py-3 text-sm">{freelancer.notes}</p>}
          </SectionCard>
          <FreelancerReviewsSection freelancerId={freelancer.id} freelancerName={freelancer.name} />
          <FreelancerLinksSection links={freelancer.links} />
          {history && history.length === 0 && <FreelancerProjectsEmpty />}
        </div>
      </div>

      {/* Mobil: Ring og E-post fast nederst, med plass til Chat-knappen til høyre. */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card py-3 pl-3 pr-32 empty:hidden md:hidden">
        <FreelancerQuickActions freelancer={freelancer} contacts={contacts} compact />
      </div>

      <FreelancerFormDialog key={editKey} open={editOpen} onOpenChange={setEditOpen} freelancer={freelancer} />
    </div>
  );
}
